/** @jsxImportSource theme-ui */
import { Container, Box, Button } from 'theme-ui';
import { useState, useEffect } from 'react';
import { ThemeProvider } from 'theme-ui';
import { StickyProvider } from 'contexts/app/app.provider';
import Head from 'next/head'
import theme from 'theme';
import Layout from 'components/layout';
import { Link } from 'components/link';
import PatternBG from 'assets/patternBG.png';
import Gallery from './gallery/gallery';
import Authorization from './auth/authorization';
import { API_BASE_URL } from "config"
import axios from 'axios'
import Video from './video/video';
import fileDownload from 'js-file-download';
import { Alert, Container as RContainer, Row, Col } from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Card from 'react-bootstrap/Card';
import { DEMO_SESSION_ID } from "config"
import VideoClipGallery from './gallery/video-clip-gallery';

export default function Session({ sessionId, defaultAccessCode }) {
    const [authenticated, setAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [accessToken, setAccessToken] = useState(null);
    const [sessionContent, setSessionContent] = useState(null);
    const genericError = "Error has occurred, please try again.";

    useEffect(() => {
        async function fetchSessionContent() {
            if (accessToken == null) {
                setAuthenticated(false)
            } else {
                try {
                    setLoading(true)
                    var headersConfig = {
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    };
                    const response = await axios.get(`${API_BASE_URL}/sessions/viewing/content`, headersConfig)
                    if (response.status == 200) {
                        setSessionContent(response.data)
                        setAuthenticated(true)
                    } else if (response.status == 401) {
                        setAccessToken(null)
                    } else {
                        throw 'session content request failure'
                    }
                } catch (ex) {
                    setErrorMessage(genericError)
                    throw ex
                } finally {
                    setLoading(false)
                }
            }
        }
        fetchSessionContent()
    }, [accessToken])

    async function download() {
        try {
            setLoading(true)
            setLoadingLabel('Downloading, please wait...')
            let config = {
                responseType: 'blob',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            };
            const response = await axios.get(`${API_BASE_URL}/sessions/download/content/images`, config)

            const fileNameId = 'filename=';
            const contentHeader = response.headers['content-disposition'];
            const suggestedFileName = contentHeader.substring(contentHeader.indexOf(fileNameId) + fileNameId.length);
            fileDownload(response.data, suggestedFileName);
        } finally {
            setLoading(false)
            setLoadingLabel('')
        }
    }

    async function downloadClips() {
        try {
            setLoading(true)
            setLoadingLabel('Downloading, please wait...')
            let config = {
                responseType: 'blob',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            };
            const response = await axios.get(`${API_BASE_URL}/sessions/download/content/videoclips`, config)

            const fileNameId = 'filename=';
            const contentHeader = response.headers['content-disposition'];
            const suggestedFileName = contentHeader.substring(contentHeader.indexOf(fileNameId) + fileNameId.length);
            fileDownload(response.data, suggestedFileName);
        } finally {
            setLoading(false)
            setLoadingLabel('')
        }
    }

    return (
        <>
            <Head>
                <meta name="robots" content="noindex" />
            </Head>
            <ThemeProvider theme={theme}>
                {!authenticated && sessionId &&
                    <Layout navHidden='true'>
                        <section sx={styles.auth}>
                            <Authorization sessionId={sessionId} defaultAccessCode={defaultAccessCode} onAuthorization={(accessToken) => { setAccessToken(accessToken) }} />
                        </section>
                    </Layout>
                }

                {authenticated &&
                    <StickyProvider>
                        <Layout navHidden='true' title={sessionContent.name} fixedFooter='true'>
                            {sessionContent != null && errorMessage === '' &&
                                <div>
                                    <section sx={styles.video}>
                                        <Container>
                                            {sessionContent.type == 'VideoAndImages' &&
                                                <Box>
                                                    {sessionContent.videoState == 'Viewable' &&
                                                        <Video
                                                            videoDownloadUrl={sessionContent.videoDownloadUrl}
                                                        />
                                                    }
                                                    {sessionContent.videoState != 'Viewable' &&
                                                        <Card>
                                                            <Card.Body style={{ minHeight: '15rem' }}>
                                                                <Card.Title>The video is not ready to view!</Card.Title>
                                                                <Card.Text>
                                                                    Please check back later.
                                                                </Card.Text>
                                                            </Card.Body>
                                                            <Card.Footer>
                                                                Video State: {sessionContent.videoState}
                                                            </Card.Footer>
                                                        </Card>
                                                    }
                                                </Box>
                                            }
                                            <div style={{ minHeight: '3rem' }}>
                                                {!loading && sessionContent.images?.length > 0 &&
                                                    <>
                                                        {sessionId != DEMO_SESSION_ID ?
                                                            <Button
                                                                sx={styles.download}
                                                                variant={'whiteButton'}
                                                                aria-label='Download'
                                                                onClick={download}>
                                                                Download Images
                                                            </Button>
                                                            :
                                                            <Button
                                                                sx={styles.download}
                                                                variant={'grayButton'}
                                                                aria-label='Download (Disabled for Demo)'>
                                                                Download Images (Disabled for Demo)
                                                            </Button>
                                                        }
                                                    </>
                                                }
                                                {!loading && sessionContent.videoClips?.length > 0 &&
                                                    <Button
                                                        sx={styles.download}
                                                        variant={'whiteButton'}
                                                        aria-label='Download'
                                                        onClick={downloadClips}>
                                                        Download Video Clips
                                                    </Button>
                                                }
                                                {sessionContent.type == 'VideoAndImages' && sessionContent.videoState == 'Viewable' &&
                                                    <>
                                                        {sessionId != DEMO_SESSION_ID ?
                                                            <Link path={sessionContent.videoDownloadUrl}>
                                                                <Button
                                                                    sx={styles.download}
                                                                    variant={'whiteButton'}
                                                                    aria-label='Download Video'>
                                                                    Download Video
                                                                </Button>
                                                            </Link>
                                                            :
                                                            <Button
                                                                disabled
                                                                sx={styles.download}
                                                                variant={'grayButton'}
                                                                aria-label='Download Video (Disabled for Demo)'>
                                                                Download Video (Disabled for Demo)
                                                            </Button>
                                                        }
                                                    </>
                                                }
                                                <div sx={styles.clearfix}></div>
                                                {loading &&
                                                    <ProgressBar className='mb-2 mt-2' variant="success" animated now={100} label={loadingLabel} />
                                                }
                                            </div>


                                        </Container>
                                    </section>

                                    <section sx={styles.images}>
                                        <Container>
                                            <Box>
                                                {sessionContent.videoClips?.length > 0 &&
                                                    <VideoClipGallery videoClips={sessionContent.videoClips} thumbnails={sessionContent.images} />
                                                }
                                            </Box>
                                        </Container>
                                    </section>

                                    <section sx={styles.images}>
                                        <Container>
                                            <Box>
                                                {sessionContent.images?.length > 0 ?
                                                    <Gallery images={sessionContent.images} />
                                                    :
                                                    <p>Images will display here after the ultrasound facility uploads the images from the session.</p>
                                                }
                                            </Box>
                                        </Container>
                                    </section>
                                </div>
                            }
                            {errorMessage != '' &&
                                <section sx={styles.video}>
                                    <Container>
                                        <Box>
                                            <RContainer>
                                                <Row className="mt-3">
                                                    <Alert variant="danger">
                                                        {errorMessage}
                                                    </Alert>
                                                </Row>
                                            </RContainer>
                                        </Box>
                                    </Container>
                                </section>
                            }
                        </Layout>
                    </StickyProvider>
                }
            </ThemeProvider>
        </>
    );
}

const styles = {
    download: {
        margin: 2,
        float: 'right'
    },
    clearfix: {
        clear: 'both'
    },
    auth: {
        paddingTop: 100,
        height: '100vh',
        backgroundColor: 'primary',
        backgroundImage: `url(${PatternBG.src})`,
        backgroundRepeat: `no-repeat`,
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        position: 'relative',
    },
    video: {
        backgroundColor: 'primary',
        backgroundImage: `url(${PatternBG.src})`,
        backgroundRepeat: `no-repeat`,
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        position: 'relative',
        paddingTop: 100
    },
    images: {
        marginTop: 25,
        mb: 75
    },
}